// Remplissage maximal des compartiments par code produit.
// Même logique que app/compartment_fill_rules.py : correspondance (casse et
// espaces ignorés, plus petit pourcentage), arrondi vers le bas à la demi-tonne
// calculé en entiers (dixièmes de tonne). Partagé par la carte des Paramètres
// système et le repère « EXP » du Gantt du Dispatch.

export const PRODUCT_COMPARTMENT_FILL_RULES_SETTING_KEY = "product_compartment_fill_rules";
export const PRODUCT_COMPARTMENT_FILL_MATCH_MODE_OPTIONS = Object.freeze([
  Object.freeze({ value: "starts_with", label: "Débute par" }),
  Object.freeze({ value: "ends_with", label: "Se termine par" }),
]);
export const PRODUCT_COMPARTMENT_FILL_MIN_PERCENT = 1;
export const PRODUCT_COMPARTMENT_FILL_MAX_PERCENT = 100;
export const PRODUCT_COMPARTMENT_FILL_PATTERN_MAX_LENGTH = 32;
export const PRODUCT_COMPARTMENT_FILL_MAX_RULES = 50;
export const PRODUCT_COMPARTMENT_FILL_EXAMPLE_CAPACITIES = Object.freeze([5, 4.5, 4, 3.5, 3, 2.5, 2]);
export const PRODUCT_COMPARTMENT_FILL_EXAMPLE_DOMPEUR_CAPACITY = 38;

const MATCH_MODE_ORDER = PRODUCT_COMPARTMENT_FILL_MATCH_MODE_OPTIONS.map((option) => option.value);
const MATCH_MODE_LABELS = Object.fromEntries(
  PRODUCT_COMPARTMENT_FILL_MATCH_MODE_OPTIONS.map((option) => [option.value, option.label]),
);

export const PRODUCT_COMPARTMENT_FILL_ERRORS = Object.freeze({
  patternRequired: "Motif requis.",
  percentInvalid: "Pourcentage entier entre 1 et 100.",
  duplicate: "Règle en double.",
  patternTooLong: `Motif limité à ${PRODUCT_COMPARTMENT_FILL_PATTERN_MAX_LENGTH} caractères.`,
});

export function normalizeProductCode(code) {
  if (code === null || code === undefined) {
    return "";
  }
  return String(code).trim().toLowerCase();
}

export function getMatchModeLabel(matchMode) {
  return MATCH_MODE_LABELS[matchMode] || String(matchMode || "");
}

function parsePercent(value) {
  if (typeof value === "number") {
    return value;
  }
  const normalizedValue = String(value ?? "").trim().replace(",", ".");
  if (!normalizedValue) {
    return Number.NaN;
  }
  return Number(normalizedValue);
}

function isValidPercent(value) {
  const percent = parsePercent(value);
  return Number.isInteger(percent)
    && percent >= PRODUCT_COMPARTMENT_FILL_MIN_PERCENT
    && percent <= PRODUCT_COMPARTMENT_FILL_MAX_PERCENT;
}

export function normalizeFillRules(rules) {
  if (!Array.isArray(rules)) {
    return [];
  }
  return rules
    .filter((rule) => rule && typeof rule === "object")
    .map((rule) => ({
      match_mode: MATCH_MODE_ORDER.includes(rule.match_mode) ? rule.match_mode : "starts_with",
      pattern: typeof rule.pattern === "string" ? rule.pattern.trim() : String(rule.pattern ?? "").trim(),
      fill_percent: parsePercent(rule.fill_percent),
    }));
}

// Ordre canonique partagé avec le backend : « Débute par » puis « Se termine par »,
// motif sans casse, pourcentage.
export function canonicalizeFillRules(rules) {
  return normalizeFillRules(rules)
    .slice()
    .sort((left, right) => {
      const byMode = MATCH_MODE_ORDER.indexOf(left.match_mode) - MATCH_MODE_ORDER.indexOf(right.match_mode);
      if (byMode !== 0) {
        return byMode;
      }
      const leftPattern = normalizeProductCode(left.pattern);
      const rightPattern = normalizeProductCode(right.pattern);
      if (leftPattern !== rightPattern) {
        return leftPattern < rightPattern ? -1 : 1;
      }
      return left.fill_percent - right.fill_percent;
    });
}

export function fillRulesEqual(left, right) {
  const resolvedLeft = canonicalizeFillRules(left);
  const resolvedRight = canonicalizeFillRules(right);
  return resolvedLeft.length === resolvedRight.length
    && resolvedLeft.every((rule, index) => (
      rule.match_mode === resolvedRight[index].match_mode
      && rule.pattern === resolvedRight[index].pattern
      && rule.fill_percent === resolvedRight[index].fill_percent
    ));
}

// Erreurs par ligne ({ pattern, fill_percent, duplicate }) dans l'ordre des lignes saisies.
export function validateFillRules(rules) {
  const rows = Array.isArray(rules) ? rules : [];
  const seenConditions = new Map();
  const errors = rows.map((rule) => {
    const rowErrors = {};
    const pattern = typeof rule?.pattern === "string" ? rule.pattern.trim() : String(rule?.pattern ?? "").trim();
    if (!pattern) {
      rowErrors.pattern = PRODUCT_COMPARTMENT_FILL_ERRORS.patternRequired;
    } else if (pattern.length > PRODUCT_COMPARTMENT_FILL_PATTERN_MAX_LENGTH) {
      rowErrors.pattern = PRODUCT_COMPARTMENT_FILL_ERRORS.patternTooLong;
    }
    if (!isValidPercent(rule?.fill_percent)) {
      rowErrors.fill_percent = PRODUCT_COMPARTMENT_FILL_ERRORS.percentInvalid;
    }
    return rowErrors;
  });
  rows.forEach((rule, index) => {
    const pattern = normalizeProductCode(rule?.pattern);
    if (!pattern) {
      return;
    }
    const condition = `${rule?.match_mode}::${pattern}`;
    if (seenConditions.has(condition)) {
      errors[index].duplicate = PRODUCT_COMPARTMENT_FILL_ERRORS.duplicate;
      errors[seenConditions.get(condition)].duplicate = PRODUCT_COMPARTMENT_FILL_ERRORS.duplicate;
      return;
    }
    seenConditions.set(condition, index);
  });
  return errors;
}

export function hasFillRuleErrors(rules) {
  const rows = Array.isArray(rules) ? rules : [];
  return rows.length > PRODUCT_COMPARTMENT_FILL_MAX_RULES
    || validateFillRules(rows).some((rowErrors) => Object.keys(rowErrors).length > 0);
}

export function ruleMatchesCode(rule, code) {
  const normalizedCode = normalizeProductCode(code);
  const pattern = normalizeProductCode(rule?.pattern);
  if (!normalizedCode || !pattern) {
    return false;
  }
  if (rule?.match_mode === "starts_with") {
    return normalizedCode.startsWith(pattern);
  }
  if (rule?.match_mode === "ends_with") {
    return normalizedCode.endsWith(pattern);
  }
  return false;
}

// Plus petit pourcentage des règles correspondantes; null si aucune limite (100 % ou plus).
export function resolveFillPercentForCode(code, rules) {
  if (!Array.isArray(rules)) {
    return null;
  }
  let resolvedPercent = null;
  rules.forEach((rule) => {
    if (!ruleMatchesCode(rule, code)) {
      return;
    }
    const percent = parsePercent(rule.fill_percent);
    if (!Number.isFinite(percent)) {
      return;
    }
    if (resolvedPercent === null || percent < resolvedPercent) {
      resolvedPercent = percent;
    }
  });
  if (resolvedPercent === null || resolvedPercent >= PRODUCT_COMPARTMENT_FILL_MAX_PERCENT) {
    return null;
  }
  return resolvedPercent;
}

// Seule fonction d'arrondi (identique au backend) : dixièmes de tonne entiers.
export function effectiveCompartmentCapacityTons(capacityTons, fillPercent) {
  if (fillPercent === null || fillPercent === undefined) {
    return capacityTons;
  }
  const capacityTenths = Math.floor(Number(capacityTons || 0) * 10 + 1e-6);
  if (capacityTenths <= 0) {
    return 0;
  }
  const effectiveTenths = Math.floor(Math.floor((capacityTenths * Number(fillPercent)) / 100) / 5) * 5;
  return effectiveTenths / 10;
}

export function formatFillTons(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "";
  }
  const rounded = Math.round(numericValue * 10) / 10;
  const label = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace(".", ",");
  return `${label} t`;
}

export function buildFillExampleLine(fillPercent) {
  const compartmentExamples = PRODUCT_COMPARTMENT_FILL_EXAMPLE_CAPACITIES.map((capacity) => (
    `${formatFillTons(capacity)} → ${formatFillTons(effectiveCompartmentCapacityTons(capacity, fillPercent))}`
  ));
  const dompeurExample = `camion sans compartiments ${formatFillTons(PRODUCT_COMPARTMENT_FILL_EXAMPLE_DOMPEUR_CAPACITY)} → ${formatFillTons(
    effectiveCompartmentCapacityTons(PRODUCT_COMPARTMENT_FILL_EXAMPLE_DOMPEUR_CAPACITY, fillPercent),
  )}`;
  return [...compartmentExamples, dompeurExample].join(" · ");
}

export function matchCatalogProducts(rule, productCodeOptions) {
  if (!Array.isArray(productCodeOptions)) {
    return [];
  }
  return productCodeOptions.filter((option) => ruleMatchesCode(rule, option?.code));
}

export function buildCatalogPreviewLabel(rule, productCodeOptions, { maxItems = 3 } = {}) {
  if (!normalizeProductCode(rule?.pattern)) {
    return "";
  }
  const matches = matchCatalogProducts(rule, productCodeOptions);
  if (!matches.length) {
    return "Aucun code produit ne correspond.";
  }
  const listed = matches
    .slice(0, maxItems)
    .map((option) => [option.code, option.label].filter(Boolean).join(" "))
    .join(", ");
  const countLabel = matches.length === 1 ? "1 code produit" : `${matches.length} codes produit`;
  return `${countLabel} : ${listed}${matches.length > maxItems ? "…" : ""}`;
}

export function createEmptyFillRule() {
  return { match_mode: "starts_with", pattern: "", fill_percent: 80 };
}

// Charge utile de mise à jour : règles canoniques, pourcentages entiers.
export function buildFillRulesPayload(rules) {
  return canonicalizeFillRules(rules).map((rule) => ({
    match_mode: rule.match_mode,
    pattern: rule.pattern,
    fill_percent: Math.trunc(rule.fill_percent),
  }));
}

// --- Gantt du Dispatch : repère « produit en expansion » ---------------------------------------

// Règles actives transmises avec les résultats du Dispatch ; champ absent ou vide, ou EXP
// désactivé pour la date (menu Plus) → aucun repère.
export function resolveCompartmentFillRulesFromResults(resultsData) {
  if (resultsData?.product_compartment_fill_enabled === false) {
    return null;
  }
  const rules = resultsData?.product_compartment_fill_rules;
  return Array.isArray(rules) && rules.length ? rules : null;
}

// Toujours sur le grain_code de la commande (jamais sur un repli raw_data) : même source que le moteur.
export function resolveOrderCompartmentFillPercent(order, rules) {
  if (!order || typeof order !== "object" || !Array.isArray(rules)) {
    return null;
  }
  return resolveFillPercentForCode(order.grain_code, rules);
}

// Commande de l'étape si connue, sinon grain_code porté par l'étape elle-même.
export function resolveStopCompartmentFill(order, stop, rules) {
  if (!Array.isArray(rules) || !rules.length) {
    return { percent: null, code: "" };
  }
  const orderCode = typeof order?.grain_code === "string" ? order.grain_code.trim() : "";
  const stopCode = typeof stop?.grain_code === "string" ? stop.grain_code.trim() : "";
  const code = orderCode || stopCode;
  const percent = resolveFillPercentForCode(code, rules);
  return { percent, code: percent === null ? "" : code };
}

export function normalizeCompartmentFillPercent(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const percent = Number(value);
  return Number.isFinite(percent) && percent > 0 && percent < PRODUCT_COMPARTMENT_FILL_MAX_PERCENT ? percent : null;
}

// Regroupement de segments : % le plus strict, son code et décompte des commandes distinctes.
export function summarizeCompartmentFillSegments(segments) {
  const resolvedSegments = Array.isArray(segments) ? segments.filter(Boolean) : [];
  const orderKeys = new Set();
  const limitedOrderKeys = new Set();
  let percent = null;
  let code = "";
  let limitedSegmentCount = 0;
  resolvedSegments.forEach((segment) => {
    const orderKey = String(segment?.operationOrderId || segment?.orderLabel || "").trim();
    if (orderKey) {
      orderKeys.add(orderKey);
    }
    const segmentPercent = normalizeCompartmentFillPercent(segment?.compartmentFillPercent);
    if (segmentPercent === null) {
      return;
    }
    limitedSegmentCount += 1;
    if (orderKey) {
      limitedOrderKeys.add(orderKey);
    }
    if (percent === null || segmentPercent < percent) {
      percent = segmentPercent;
      code = typeof segment?.compartmentFillProductCode === "string" ? segment.compartmentFillProductCode : "";
    }
  });
  const limitedCount = limitedOrderKeys.size || (limitedSegmentCount ? 1 : 0);
  return {
    code,
    limitedCount,
    percent,
    totalCount: Math.max(orderKeys.size, limitedCount),
  };
}

function formatImpactTons(value) {
  return `${String(Math.round(value * 100) / 100).replace(".", ",")} t`;
}

// Hovercard « Expansion » : calcul par compartiment des chargements limités. Sans compartiments
// (dompeur), la benne prend la capacité du camion, comme le moteur.
export function buildCompartmentFillImpact(segments, { truckCompartments = [], truckCapacityTons = null } = {}) {
  const compartments = Array.isArray(truckCompartments) ? truckCompartments.filter(Boolean) : [];
  const rowsByKey = new Map();
  const orderLabels = [];
  const codes = new Set();
  const percents = new Set();
  (Array.isArray(segments) ? segments : []).forEach((segment) => {
    const percent = normalizeCompartmentFillPercent(segment?.compartmentFillPercent);
    if (percent === null) {
      return;
    }
    percents.add(percent);
    if (String(segment?.compartmentFillProductCode || "").trim()) {
      codes.add(String(segment.compartmentFillProductCode).trim());
    }
    const orderLabel = String(segment?.orderLabel || segment?.operationOrderId || "").trim();
    if (!orderLabels.includes(orderLabel)) {
      orderLabels.push(orderLabel);
    }
    (Array.isArray(segment?.compartmentAllocations) ? segment.compartmentAllocations : []).forEach((allocation) => {
      const quantityTons = Number(allocation?.quantity_tons);
      if (!(quantityTons > 0)) {
        return;
      }
      const compartment = compartments.find((item) => allocation.compartment_id && item.compartment_id === allocation.compartment_id)
        || compartments.find((item) => Number(item.position) === Number(allocation.position));
      const position = Number(allocation.position ?? compartment?.position);
      const compartmentKey = allocation.compartment_id || position;
      const key = `${orderLabel}::${compartmentKey}`;
      const row = rowsByKey.get(key) || {
        capacityTons: Number(compartments.length ? compartment?.capacity_tons : truckCapacityTons),
        compartmentKey,
        loadedTons: 0,
        orderIndex: orderLabels.indexOf(orderLabel),
        orderLabel,
        percent,
        position,
      };
      row.loadedTons += quantityTons;
      rowsByKey.set(key, row);
    });
  });
  // Compartiment partagé : la limite vaut pour la somme des commandes chargées ensemble.
  const loadedTonsByCompartment = new Map();
  rowsByKey.forEach((row) => {
    loadedTonsByCompartment.set(row.compartmentKey, (loadedTonsByCompartment.get(row.compartmentKey) || 0) + row.loadedTons);
  });
  const rows = [...rowsByKey.values()]
    .sort((left, right) => left.orderIndex - right.orderIndex || left.position - right.position)
    .map(({ capacityTons, compartmentKey, loadedTons, orderLabel, percent, position }) => {
      const hasCapacity = Number.isFinite(capacityTons) && capacityTons > 0;
      const maxTons = hasCapacity ? effectiveCompartmentCapacityTons(capacityTons, percent) : null;
      return {
        calculationLabel: hasCapacity ? `${formatImpactTons(capacityTons)} × ${percent} % = ${formatImpactTons((capacityTons * percent) / 100)}` : "—",
        compartmentLabel: compartments.length ? (Number.isFinite(position) ? `C${position}` : "—") : "Benne",
        exceedsMax: hasCapacity && loadedTonsByCompartment.get(compartmentKey) > maxTons + 1e-6,
        loadedLabel: formatImpactTons(loadedTons),
        maxLabel: hasCapacity ? formatImpactTons(maxTons) : "—",
        orderLabel,
      };
    });
  if (!percents.size) {
    return null;
  }
  const codeLabel = codes.size ? `${codes.size > 1 ? "Codes" : "Code"} ${[...codes].join(", ")} : au plus` : "Au plus";
  const percentLabel = percents.size > 1 ? "le pourcentage indiqué" : `${[...percents][0]} %`;
  return {
    exceedsNote: rows.some((row) => row.exceedsMax) ? "Au-delà du maximum : quantité conservée telle quelle d'un plan existant." : "",
    note: `${codeLabel} ${percentLabel} de chaque compartiment, arrondi vers le bas à la demi-tonne.`,
    rows,
    showOrder: orderLabels.length > 1,
  };
}

export function buildCompartmentFillIndicatorLabel(percent) {
  if (percent === null || percent === undefined) {
    return "";
  }
  return `Expansion ${percent} %`;
}
